import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Settings, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface SettingsData {
  id: number;
  brand_id: number;
  is_enabled: boolean;
  daily_digest_enabled: boolean;
  weekly_digest_enabled: boolean;
  creative_tracking_enabled: boolean;
  conversation_extraction_enabled: boolean;
  competitor_intel_enabled: boolean;
  product_tracking_enabled: boolean;
  max_chunks_per_account: number;
  chunk_retention_days: number;
}

interface HeartbeatSettingsProps {
  brandId: number;
}

export function HeartbeatSettings({ brandId }: HeartbeatSettingsProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery<SettingsData>({
    queryKey: ["/api/heartbeat/brands", brandId, "settings"],
    queryFn: async () => {
      const res = await fetch(`/api/heartbeat/brands/${brandId}/settings`);
      if (!res.ok) throw new Error("Failed to fetch settings");
      return res.json();
    },
    enabled: open && !!brandId,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, boolean | number>) => {
      const res = await fetch(`/api/heartbeat/brands/${brandId}/settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/brands", brandId, "settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/brands", brandId] });
    },
  });

  const handleClearAll = async () => {
    setClearing(true);
    setClearError(null);
    try {
      let hasMore = true;
      let failCount = 0;
      const maxFails = 5;
      while (hasMore && failCount < maxFails) {
        const res = await fetch(`/api/heartbeat/brands/${brandId}/chunks?limit=200`);
        if (!res.ok) {
          setClearError("Failed to fetch chunks for deletion");
          break;
        }
        const { chunks } = await res.json();
        if (!chunks || chunks.length === 0) {
          hasMore = false;
          break;
        }
        for (const chunk of chunks) {
          const delRes = await fetch(`/api/heartbeat/chunks/${chunk.id}`, { method: "DELETE" });
          if (!delRes.ok) {
            failCount++;
            if (failCount >= maxFails) {
              setClearError("Too many deletion failures. Some chunks may remain.");
              hasMore = false;
              break;
            }
          }
        }
        if (chunks.length < 200) {
          hasMore = false;
        }
      }
      queryClient.invalidateQueries({ queryKey: ["/api/heartbeat/brands", brandId] });
    } catch {
      setClearError("Network error while clearing memory");
    } finally {
      setClearing(false);
    }
  };

  const toggleSetting = (key: string, value: boolean | number) => {
    updateMutation.mutate({ [key]: value });
  };

  const s = settings;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-white hover:bg-gray-50 text-xs gap-1.5 border border-[#DCDCDC] text-[#8C8578] hover:border-[#A8A49E]" data-testid="button-heartbeat-settings">
          <Settings className="h-3.5 w-3.5" />
          Settings
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-indigo-500" />
            Heartbeat Settings
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
          </div>
        ) : s ? (
          <div className="space-y-5 pt-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Heartbeat Enabled</p>
                <p className="text-xs text-gray-500">Turn on AI intelligence memory</p>
              </div>
              <Switch
                checked={s.is_enabled}
                onCheckedChange={v => toggleSetting("isEnabled", v)}
                data-testid="toggle-heartbeat-enabled"
              />
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pipelines</p>
              {[
                { key: "dailyDigestEnabled", dbKey: "daily_digest_enabled", label: "Daily Performance Digest", desc: "Anomaly detection & daily summary" },
                { key: "weeklyDigestEnabled", dbKey: "weekly_digest_enabled", label: "Weekly Synthesis", desc: "Cross-cutting strategic insights" },
                { key: "creativeTrackingEnabled", dbKey: "creative_tracking_enabled", label: "Creative Tracking", desc: "Ad creative pattern analysis" },
                { key: "competitorIntelEnabled", dbKey: "competitor_intel_enabled", label: "Competitor Intelligence", desc: "Insights from competitor analysis" },
                { key: "productTrackingEnabled", dbKey: "product_tracking_enabled", label: "Product Intelligence", desc: "Product data as knowledge chunks" },
                { key: "conversationExtractionEnabled", dbKey: "conversation_extraction_enabled", label: "Conversation Extraction", desc: "Learn from AskMoji chats" },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-700">{item.label}</p>
                    <p className="text-[11px] text-gray-400">{item.desc}</p>
                  </div>
                  <Switch
                    checked={s[item.dbKey as keyof SettingsData] as boolean}
                    onCheckedChange={v => toggleSetting(item.key, v)}
                    disabled={!s.is_enabled}
                    data-testid={`toggle-${item.key}`}
                  />
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-700">Max Chunks</p>
                  <span className="text-xs font-medium text-indigo-600">{s.max_chunks_per_account}</span>
                </div>
                <Slider
                  value={[s.max_chunks_per_account]}
                  onValueChange={([v]) => toggleSetting("maxChunksPerAccount", v)}
                  min={500}
                  max={5000}
                  step={100}
                  disabled={!s.is_enabled}
                  data-testid="slider-max-chunks"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-700">Retention Days</p>
                  <span className="text-xs font-medium text-indigo-600">{s.chunk_retention_days}</span>
                </div>
                <Slider
                  value={[s.chunk_retention_days]}
                  onValueChange={([v]) => toggleSetting("chunkRetentionDays", v)}
                  min={30}
                  max={365}
                  step={30}
                  disabled={!s.is_enabled}
                  data-testid="slider-retention"
                />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-2">
              {clearError && (
                <p className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{clearError}</p>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-red-500 hover:text-red-600 hover:border-red-200 gap-1.5"
                    disabled={clearing}
                    data-testid="button-clear-memory"
                  >
                    {clearing ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3" />}
                    Clear All Memory
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear all memory?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently archive all knowledge chunks for this brand. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="button-cancel-clear">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleClearAll}
                      className="bg-red-500 hover:bg-red-600"
                      data-testid="button-confirm-clear"
                    >
                      Clear All
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
